import { FC, useRef, useEffect, useState } from 'react'
import { ActiveFilter, DateFilterValue } from '../Devices'
import FilterChip from './FilterChip'
import OtherFilters from './OtherFilters'
import { FilterChipsWrapper } from './filterChipContainer.styles'
import dayjs from 'dayjs'

interface Props {
  activeFilters: ActiveFilter[]
  handleResetFilter: (colName: string) => void
}

const FilterChipsContainer: FC<Props> = ({ activeFilters, handleResetFilter }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleFilters, setVisibleFilters] = useState<ActiveFilter[]>(activeFilters)
  const [hiddenFilters, setHiddenFilters] = useState<ActiveFilter[]>([])

  useEffect(() => {
    const calculateVisibleFilters = () => {
      if (!containerRef.current) return

      const container = containerRef.current
      const containerWidth = container.offsetWidth

      // Reserve space for Reset All button (estimated 100px) and gaps
      const RESET_BUTTON_WIDTH = 100
      const GAP_WIDTH = 16 // theme.spacing.md
      const CONTROLS_MARGIN = 24 // 24px gap before controls on right

      const availableWidth = containerWidth - RESET_BUTTON_WIDTH - CONTROLS_MARGIN - GAP_WIDTH * 2

      let currentWidth = 0
      let visibleCount = 0

      const tempDiv = document.createElement('div')
      tempDiv.style.visibility = 'hidden'
      tempDiv.style.position = 'absolute'
      tempDiv.style.whiteSpace = 'nowrap'
      document.body.appendChild(tempDiv)

      for (let i = 0; i < activeFilters.length; i++) {
        const filter = activeFilters[i]
        const formattedValue = formatFilterValue(filter)

        // Create a more realistic chip element for measurement
        const chip = document.createElement('div')
        chip.className = 'MuiButton-root filter-chip-button'
        chip.style.display = 'inline-flex'
        chip.style.alignItems = 'center'
        chip.style.padding = '14px 12px'
        chip.style.border = '1px solid #4e444b'
        chip.style.borderRadius = '8px'
        chip.style.fontSize = '16px'
        chip.style.fontWeight = 'bold'
        chip.style.fontFamily = 'inherit'
        chip.style.gap = '8px'

        // Truncate text if too long
        const maxTextLength = 40 // Adjust based on your needs
        const displayText =
          formattedValue.length > maxTextLength ? `${formattedValue.substring(0, maxTextLength)}...` : formattedValue

        chip.innerHTML = `
          <span style="color: #a88ca4; margin-right: 8px;">${filter.filterName}:</span>
          <span style="color: white;">${displayText}</span>
          <span style="width: 18px; height: 18px; margin-left: 4px;">×</span>
        `

        tempDiv.appendChild(chip)
        const chipWidth = chip.offsetWidth + GAP_WIDTH

        if (currentWidth + chipWidth <= availableWidth) {
          currentWidth += chipWidth
          visibleCount++
        } else {
          break
        }
      }

      document.body.removeChild(tempDiv)

      const uniqueFilters = activeFilters.reduce((acc: ActiveFilter[], cur) => {
        if (!acc.find((item) => item.filterName === cur.filterName)) {
          acc.push(cur)
        }
        return acc
      }, []) as ActiveFilter[]

      setVisibleFilters(uniqueFilters.slice(0, visibleCount))
      setHiddenFilters(uniqueFilters.slice(visibleCount))
    }

    calculateVisibleFilters()

    const resizeObserver = new ResizeObserver(calculateVisibleFilters)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current)
      }
    }
  }, [activeFilters])

  const formatFilterValue = (filter: ActiveFilter, maxChars = 30): string => {
    const { filterValue } = filter

    // Handle date filters
    if (typeof filterValue === 'object' && !Array.isArray(filterValue)) {
      const dateFilter = filterValue as DateFilterValue

      if (dateFilter.type === 'before') {
        return `Before ${dayjs(dateFilter.dateFrom).format('MM/DD/YYYY')}`
      } else if (dateFilter.type === 'after') {
        return `After ${dayjs(dateFilter.dateFrom).format('MM/DD/YYYY')}`
      } else if (dateFilter.type === 'inRange') {
        return `${dayjs(dateFilter.dateFrom).format('MM/DD/YYYY')} - ${dayjs(dateFilter.dateTo).format('MM/DD/YYYY')}`
      }
    }

    // Handle regular array values - fit as many as possible within maxChars
    if (Array.isArray(filterValue)) {
      if (filterValue.length === 1) {
        const val = filterValue[0]
        // Truncate single long value
        if (val.length > maxChars) {
          return `${val.substring(0, maxChars)}...`
        }
        return val
      }

      // Try to fit as many values as possible
      let result = ''
      let includedCount = 0
      const remaining = filterValue.length

      for (let i = 0; i < filterValue.length; i++) {
        const val = filterValue[i]
        const separator = includedCount > 0 ? ', ' : ''
        const remainingCount = remaining - includedCount - 1
        const suffixLength = remainingCount > 0 ? ` (+${remainingCount})`.length : 0

        // Check if adding this value (possibly truncated) would fit
        const availableSpace = maxChars - result.length - separator.length - suffixLength

        if (availableSpace <= 0 && includedCount > 0) {
          // No more space, show remaining count
          break
        }

        if (val.length <= availableSpace) {
          // Value fits completely
          result += separator + val
          includedCount++
        } else if (includedCount === 0) {
          // First value doesn't fit - truncate it
          const truncatedVal = val.substring(0, Math.max(availableSpace - 3, 10)) + '...'
          result += truncatedVal
          includedCount++
          break
        } else {
          // Subsequent value doesn't fit - stop here
          break
        }
      }

      const remainingCount = remaining - includedCount
      if (remainingCount > 0) {
        result += ` (+${remainingCount})`
      }

      return result
    }

    return String(filterValue)
  }

  return (
    <FilterChipsWrapper className="filter-chips-container" ref={containerRef}>
      {visibleFilters.map((filter, idx: number) => (
        <FilterChip
          key={idx}
          filterName={filter.filterName}
          filterValue={filter.filterValue}
          displayValue={formatFilterValue(filter)}
          handleResetFilter={handleResetFilter}
        />
      ))}
      {hiddenFilters.length > 0 && <OtherFilters hiddenFilters={hiddenFilters} handleResetFilter={handleResetFilter} />}
      {activeFilters.length > 0 && (
        <FilterChip isResetButton filterName="" filterValue="" handleResetFilter={handleResetFilter} />
      )}
    </FilterChipsWrapper>
  )
}

export default FilterChipsContainer
